/**
 * Einheitlicher Wortlaut fuer alle Aktionen, die im Lesemodus nicht laufen.
 * Wichtig: nie stillschweigend abbrechen — der Nutzer soll den Grund sehen.
 *
 * Der Wortlaut stammt aus SiGeKoInspectionModule/ProjectArchiveView, wo er
 * zuerst entstanden ist; hier liegt er zentral, damit neue Riegel nicht jedes
 * Mal neu formuliert werden. Die beiden Altstellen halten ihre eigenen Kopien
 * mit identischem Text und koennen spaeter hierher umgezogen werden.
 */
export const READ_ONLY_HINWEIS = 'Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet — Änderungen werden nicht gespeichert.';

/**
 * Der Ausweg gehoert in JEDE Absage: sonst sagen alle Meldungen nur, was
 * nicht geht, aber keine, wie man in den Schreibmodus kommt.
 */
export const READ_ONLY_AUSWEG = 'Zum Bearbeiten das Projekt über die Projektliste öffnen.';

/**
 * ─── Derselbe Riegel, anderer Grund ────────────────────────────────────────
 *
 * Es gibt ZWEI Wege in den Lesemodus, und sie brauchen verschiedene Saetze:
 *
 *   * ausdruecklich schreibgeschuetzt geoeffnet („Offene Positionen") — dann
 *     stimmen READ_ONLY_HINWEIS und READ_ONLY_AUSWEG woertlich.
 *   * ein anderer haelt die Projektsperre — dann stimmt BEIDES nicht: das
 *     Projekt wurde ganz normal geoeffnet, und ein erneutes Oeffnen ueber die
 *     Projektliste aendert daran gar nichts. Der Balken formulierte das
 *     laengst richtig, die Absage-Dialoge sagten weiter den falschen Grund und
 *     schickten den Nutzer auf einen Weg, der ihn nicht weiterbringt.
 *
 * Deshalb tragen readOnlyMessage/readOnlyTitle einen optionalen Halternamen:
 * ist er da, wird der Sperrfall formuliert. Optional, damit die vielen
 * Aufrufstellen ohne Sperrbezug unveraendert bleiben.
 */
export const lockedReadOnlyHinweis = (holder: string) =>
    `Nur-Ansicht: ${holder} bearbeitet dieses Projekt gerade — Änderungen werden nicht gespeichert.`;

/**
 * Der Ausweg im Sperrfall: warten oder uebernehmen. „Ueber die Projektliste
 * oeffnen" waere hier ein Ratschlag ins Leere — er nimmt die Sperre nicht weg.
 */
export const LOCK_AUSWEG =
    'Sobald die andere Person fertig ist, lässt sich die Bearbeitung über den Hinweisbalken übernehmen.';

/** Absage-Text fuer einen konkreten Vorgang: Hinweis + Detail + Ausweg. */
export const readOnlyMessage = (detail: string, holder?: string | null) =>
    holder
        ? `${lockedReadOnlyHinweis(holder)}\n\n${detail}\n\n${LOCK_AUSWEG}`
        : `${READ_ONLY_HINWEIS}\n\n${detail}\n\n${READ_ONLY_AUSWEG}`;

/** Kurzform fuer `title`-Attribute an deaktivierten Schaltflaechen. */
export const readOnlyTitle = (detail: string, holder?: string | null) =>
    holder
        ? `${lockedReadOnlyHinweis(holder)} ${detail} ${LOCK_AUSWEG}`
        : `${READ_ONLY_HINWEIS} ${detail} ${READ_ONLY_AUSWEG}`;

/**
 * ─── Bannertexte ───────────────────────────────────────────────────────────
 *
 * Der gelbe Balken stand bisher zweimal als JSX-Kopie im Code (App.tsx und
 * SiGeKoInspectionModule). Seit ProjectNoticeBanner gibt es nur noch EIN
 * Markup — und damit soll es auch nur noch EINEN Ort fuer den Wortlaut geben.
 *
 * Ohne Fettung: der Balken traegt schon `font-bold`, ein `<b>` darin war
 * wirkungslos. Dadurch bleiben die Texte einfache Zeichenketten und sind
 * ohne DOM testbar (das Projekt hat kein jsdom).
 */
export const READ_ONLY_BANNER =
    'Nur-Ansicht (aus „Offene Positionen" geöffnet): Ansehen, PDF-Vorschau und Download ' +
    `möglich. Eingaben werden nicht gespeichert. ${READ_ONLY_AUSWEG}`;

/** Wie oben, plus die im Pruefmodul zusaetzlich gesperrten Wege. */
export const READ_ONLY_BANNER_MODUL =
    'Nur-Ansicht (aus „Offene Positionen" geöffnet): Ansehen, PDF-Vorschau und Download ' +
    'möglich. Eingaben werden nicht gespeichert, Versand und Abrechnung sind gesperrt. ' +
    READ_ONLY_AUSWEG;

/**
 * ─── Sperrfall ─────────────────────────────────────────────────────────────
 *
 * Eigener Wortlaut, weil der Grund ein anderer ist: nicht „so geoeffnet",
 * sondern „jemand anderes ist gerade drin". Der Ausweg lautet hier nicht
 * „ueber die Projektliste oeffnen" (das aendert nichts), sondern „warten oder
 * uebernehmen" — deshalb steht READ_ONLY_AUSWEG bewusst NICHT mit dabei.
 */
export const LOCK_KEINE_SPEICHERUNG = 'Änderungen werden nicht gespeichert.';

/** Fallback, wenn die Sperrzeile keinen Anzeigenamen hergibt. */
export const LOCK_OHNE_HALTER = 'Das Projekt wird gerade von einem anderen Benutzer bearbeitet.';

/**
 * Die fremde Sperre ist abgelaufen oder freigegeben. Bewusst nicht „frei" als
 * Erfolgsmeldung, sondern mit dem Hinweis, dass erst die Uebernahme wieder
 * speichert — sonst tippt jemand weiter und wundert sich.
 */
export const LOCK_FREI_GEWORDEN =
    'Die Bearbeitung durch die andere Person ist beendet. Änderungen werden erst nach der Übernahme gespeichert.';

export const LOCK_UEBERNEHMEN_LABEL = 'Bearbeitung übernehmen';
export const LOCK_AUFBRECHEN_LABEL = 'Sperre aufheben und übernehmen';

/**
 * Rueckfrage vor dem Aufbrechen einer FRISCHEN fremden Sperre (nur Admin).
 *
 * Der Wortlaut muss der Wirklichkeit entsprechen, und die ist nicht „ab jetzt
 * bist du allein drin": der Halter erfaehrt vom Aufbrechen ueber Realtime oder
 * spaetestens beim naechsten Herzschlag — das sind in aller Regel Sekunden,
 * bei totem Socket bis zu 45 s. Bis dahin tippt er weiter, und was er in
 * diesem Fenster speichert, liegt hinterher oben.
 *
 * Frueher versprach dieser Text das Gegenteil („der zuletzt Speichernde
 * gewinnt" klang nach: Sie, weil Sie gerade uebernehmen). Genau das war
 * falsch — der zuletzt Speichernde kann sehr wohl der andere sein.
 */
export const lockTakeoverConfirm = (holder: string): string =>
    `Die Bearbeitung liegt derzeit bei ${holder}.\n\n` +
    'Diese Person wird in die Nur-Ansicht versetzt, sobald die Übernahme sie erreicht — ' +
    'meist binnen Sekunden, bei schlechter Verbindung bis zu einer Minute. Was sie bis dahin ' +
    'speichert, liegt anschließend über Ihrem Stand; was sie noch nicht hochgeladen hat, ' +
    'bleibt liegen und geht nicht mit ein.\n\n' +
    'Bearbeitung wirklich übernehmen?';

/**
 * Der Neulade-Schritt der Uebernahme ist fehlgeschlagen (offline, Ladefehler).
 *
 * Die Uebernahme wird dann komplett zurueckgenommen — Sperre wieder frei,
 * Lesemodus bleibt. Anders herum saesse der Uebernehmende auf dem alten Stand,
 * den er beim Zuschauen im Speicher hatte, und ueberschriebe damit alles, was
 * der Vorgaenger geschrieben hat.
 */
export const LOCK_UEBERNAHME_OHNE_NEULADEN =
    'Die Bearbeitung wurde NICHT übernommen: der aktuelle Projektstand konnte nicht geladen werden. ' +
    'Bitte die Verbindung prüfen und erneut versuchen.';

/**
 * ─── Fremdes Projekt (Fremdzugriff mit Begruendung, SQL Nr. 128) ───────────
 *
 * Dritter Weg in den Lesemodus: das Projekt gehoert jemand anderem, und der
 * Betrachter ist dort weder Eigentuemer noch Vertreter noch Urlaubsvertretung.
 * Ansehen darf er frei; die erste Begehung oder Buchung verlangt eine kurze
 * Begruendung, die bis Mitternacht (Europe/Berlin) fuer dieses Projekt gilt.
 * Eigentuemer und Verantwortliche bekommt die RPC mitgeteilt, nicht der Client.
 *
 * Kurz gehalten, weil der Balken auch auf dem Handy in zwei Zeilen passen
 * soll. Der Knopf daneben traegt die Handlung, der Satz nur den Grund.
 */
export const FOREIGN_BANNER =
    'Fremdes Projekt – nur Ansicht. Für Begehung oder Buchung kurz begründen; ' +
    'Eigentümer und Verantwortliche werden informiert.';

/** Aufschrift des Knopfes im Balken und des Hauptknopfes im Dialog. */
export const FOREIGN_JUSTIFY_LABEL = 'Begründen und bearbeiten';

/** Abbrechen im Dialog: das Projekt bleibt offen, nur eben zum Ansehen. */
export const FOREIGN_VIEW_ONLY_LABEL = 'Nur ansehen';

/** Hinweiszeile im Dialog — Geltungsdauer und wer davon erfaehrt. */
export const FOREIGN_DIALOG_INFO =
    'Gilt heute bis 24:00 Uhr. Eigentümer und Verantwortliche werden benachrichtigt.';

/**
 * Antwort an einen Buchungsdialog, der ein Ergebnis erwartet (Monatspauschale),
 * waehrend statt der Buchung der Begruendungsdialog aufgeht.
 */
export const FOREIGN_ERST_BEGRUENDEN =
    'Fremdes Projekt: bitte zuerst kurz begründen — danach lässt sich buchen.';

/**
 * Die Freigabe von heute ist abgelaufen, waehrend das Projekt offen war.
 *
 * Das Projekt faellt dann von selbst in die Nur-Ansicht zurueck; der Satz
 * erklaert, warum der Balken wieder dasteht, und nennt den Ausweg. Eingaben
 * von vor dem Ablauf liegen in der Warteschlange und gehen nach der naechsten
 * Begruendung hoch — deshalb keine Warnung, sondern ein Hinweis.
 */
export const FOREIGN_FREIGABE_ABGELAUFEN =
    'Die Freigabe für dieses fremde Projekt ist um 24:00 Uhr abgelaufen. ' +
    'Für weitere Begehungen oder Buchungen bitte erneut begründen.';

/**
 * Die Begruendung ist angenommen, der frische Projektstand kam aber nicht an.
 *
 * Wie bei der Uebernahme bleibt das Projekt dann im Lesemodus: auf dem Stand
 * weiterzuarbeiten, der beim Ansehen im Speicher lag, hiesse, alles zu
 * ueberschreiben, was inzwischen geschrieben wurde. Ein zweiter Versuch
 * kostet keine zweite Mitteilung — die Freigabe von heute gilt bereits.
 */
export const FOREIGN_OHNE_NEULADEN =
    'Die Begründung ist gespeichert, aber der aktuelle Projektstand konnte nicht geladen werden. ' +
    'Das Projekt bleibt in der Nur-Ansicht — bitte die Verbindung prüfen und erneut versuchen.';

/**
 * Ein lokal gesicherter Stand kann nicht hochgeladen werden, weil inzwischen
 * ein anderer das Projekt bearbeitet (typisch nach einem laengeren Netzausfall:
 * die eigene Sperre ist abgelaufen, jemand anderes hat uebernommen).
 *
 * Bewusst eine MELDUNG und keine Rueckfrage — hier ist nichts zu entscheiden.
 * Die Arbeit bleibt in der Warteschlange und geht automatisch raus, sobald das
 * Projekt wieder frei ist. Der Satz muss beides sagen: warum es haengt und
 * dass sich der Nutzer darum nicht kuemmern muss.
 */
export const queuedUploadBlockedMessage = (holder: string): string =>
    `Ihre Änderungen konnten noch nicht hochgeladen werden, weil ${holder} das Projekt gerade ` +
    'bearbeitet. Sie sind lokal gesichert; es wird automatisch erneut versucht.';

/**
 * Absage eines schreibenden Hintergrundvorgangs (Rechnungslauf), weil ein
 * anderer das Projekt gerade bearbeitet.
 *
 * Muss beides sagen: WER blockiert und dass NICHTS passiert ist. Ohne den
 * zweiten Halbsatz muss der Anwender raten, ob die Rechnung nun halb
 * ausgestellt in der Welt steht.
 */
export const lockedProjectWriteMessage = (holder: string): string =>
    `Projekt wird gerade von ${holder} bearbeitet. ` +
    'Die Rechnung wurde nicht ausgestellt — bitte später erneut versuchen.';

/** Absage, wenn die Uebernahme am Server scheiterte. */
export const LOCK_UEBERNAHME_FEHLGESCHLAGEN =
    'Die Bearbeitung konnte nicht übernommen werden. Möglicherweise ist jemand anderes zuvorgekommen.';

/**
 * Das Projekt wurde nach langer Untaetigkeit von selbst geschlossen und die
 * Sperre freigegeben (Regel: `shouldExitForIdle` in projectLockLogic).
 *
 * Der Satz muss drei Fragen beantworten, bevor sie jemand stellt: Was ist
 * passiert, ist meine Arbeit weg, und wie komme ich zurueck. Der Rueckzug
 * faellt nur bei gesichertem Speicherstand (`isSaveSettled`) — die Zusage im
 * zweiten Satz ist also keine Beruhigungsfloskel, sondern die Bedingung, unter
 * der es ueberhaupt dazu kommt.
 */
export const idleReleaseNotice = (minutes: number): string =>
    `Das Projekt wurde nach ${minutes} Minuten ohne Eingabe automatisch geschlossen und für Ihre ` +
    'Kollegen freigegeben. Alle Änderungen sind gespeichert — über die Projektliste geht es ' +
    'jederzeit weiter.';

/**
 * Vorwarnung vor dem Leerlauf-Rueckzug, mit laufender Restzeit.
 *
 * Muss in einem Satz sagen, was gleich passiert und dass es abwendbar ist —
 * die Aufschrift des Knopfes daneben (`IDLE_KEEP_ALIVE_LABEL`) traegt die
 * halbe Botschaft. Kein „Achtung" und kein Ausrufezeichen: es geht nichts
 * verloren, das Projekt laesst sich sofort wieder oeffnen.
 */
export const idleWarningNotice = (countdown: string): string =>
    `Keine Eingabe seit einer Weile: Das Projekt wird in ${countdown} automatisch geschlossen ` +
    'und für Ihre Kollegen freigegeben.';

/**
 * Aufschrift des Knopfes, der die Frist zurueckstellt.
 *
 * „Ich bin noch da" und nicht „Abbrechen": abgebrochen wird hier nichts, und
 * der Satz benennt genau die Frage, die die Anwendung gerade stellt.
 */
export const IDLE_KEEP_ALIVE_LABEL = 'Ich bin noch da';
